import * as React from "react";
import { type DialogProps } from "@radix-ui/react-dialog";
import { Command as CommandPrimitive } from "cmdk";

import { cn } from "@/lib/utils";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { RiSearchLine } from "@remixicon/react";

interface SqCommandProps extends React.ComponentPropsWithoutRef<typeof CommandPrimitive> {
  bgColor?: string;
  outlineColor?: string;
  radius?: number;
  shadow?: "none" | "sm" | "md" | "lg" | "xl";
}

const SqCommand = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive>,
  SqCommandProps
>(({ className, bgColor, outlineColor, radius = 8, shadow, style, ...props }, ref) => {
  const customCssVars = {
    ...(style || {}),
    ...(bgColor ? { "--sq-fill": bgColor } : {}),
    ...(outlineColor ? { "--sq-outline": outlineColor } : {}),
    "--sq-radius": `${radius}px`,
  } as React.CSSProperties & Record<string, string>;

  const colorBindingClasses = cn(
    bgColor && "[--squircle-fill:var(--sq-fill)!important]",
    outlineColor && "before:[--squircle-fill:var(--sq-outline)!important]",
  );

  const radiusClasses = cn(
    `[--squircle-radius:var(--sq-radius)!important]`,
    `before:[--squircle-radius:var(--sq-radius)!important]`,
  );

  const shadowClasses = cn(
    shadow === "sm" && "drop-shadow-sm",
    shadow === "md" && "drop-shadow-md",
    shadow === "lg" && "drop-shadow-lg",
    shadow === "xl" && "drop-shadow-xl",
  );

  const outlineClasses = cn(
    "relative before:absolute before:inset-0 before:squircle before:squircle-radius-[8px] before:squircle-outline-[1px] before:squircle-outline-color-border-gray before:pointer-events-none",
  );

  return (
    <CommandPrimitive
      ref={ref}
      className={cn(
        "squircle squircle-radius-[8px] squircle-smooth-[1] squircle-fill-[white] transition-all drop-shadow-md py-[2px]",
        shadowClasses,
        outlineClasses,
        colorBindingClasses,
        radiusClasses,
        "flex h-full w-full flex-col overflow-hidden bg-popover text-popover-foreground",
        className,
      )}
      style={customCssVars}
      {...props}
    />
  );
});
SqCommand.displayName = CommandPrimitive.displayName;

const SqCommandDialog = ({ children, ...props }: DialogProps) => {
  return (
    <Dialog {...props}>
      <DialogContent className="overflow-hidden p-0">
        <SqCommand className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5">
          {children}
        </SqCommand>
      </DialogContent>
    </Dialog>
  );
};

interface SqCommandInputProps
  extends React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input> {
  wrapperClass?: string;
  iconClass?: string;
  bgColor?: string;
  outlineColor?: string;
  radius?: number;
}

const SqCommandInput = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Input>,
  SqCommandInputProps
>(
  (
    {
      className,
      wrapperClass,
      iconClass,
      bgColor,
      outlineColor,
      radius = 6,
      style,
      ...props
    },
    ref,
  ) => {
    const customCssVars = {
      ...(style || {}),
      ...(bgColor ? { "--sq-fill": bgColor } : {}),
      ...(outlineColor ? { "--sq-outline": outlineColor } : {}),
      "--sq-radius": `${radius}px`,
    } as React.CSSProperties & Record<string, string>;

    const colorBindingClasses = cn(
      bgColor && "[--squircle-fill:var(--sq-fill)!important]",
      outlineColor && "before:[--squircle-fill:var(--sq-outline)!important]",
    );

    const radiusClasses = cn(
      `[--squircle-radius:var(--sq-radius)!important]`,
      `before:[--squircle-radius:var(--sq-radius)!important]`,
    );

    const outlineClasses = cn(
      "before:absolute before:inset-0 before:squircle before:squircle-radius-[6px] before:squircle-outline-[1px] before:squircle-outline-color-border-gray before:pointer-events-none",
    );

    return (
      <div
        className={cn(
          "flex items-center px-3 mx-1 mt-[2px] mb-0 h-7 squircle squircle-fill-selected-gray squircle-radius-[8px]",
          wrapperClass,
        )}
        cmdk-input-wrapper=""
      >
        <RiSearchLine
          size={14}
          color="#02185966"
          className={cn("mr-2 shrink-0", iconClass)}
        />
        <CommandPrimitive.Input
          ref={ref}
          className={cn(
            "flex w-full bg-transparent py-3 relative top-[1px] text-sm outline-none transition-all disabled:cursor-not-allowed disabled:opacity-50 text-brand-primary-new placeholder:text-brand-primary-new/40",
            outlineClasses,
            colorBindingClasses,
            radiusClasses,
            className,
          )}
          style={customCssVars}
          {...props}
        />
      </div>
    );
  },
);

SqCommandInput.displayName = CommandPrimitive.Input.displayName;

const SqCommandList = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.List>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.List
    ref={ref}
    className={cn("max-h-[300px] overflow-y-auto overflow-x-hidden", className)}
    {...props}
  />
));
SqCommandList.displayName = CommandPrimitive.List.displayName;

const SqCommandEmpty = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Empty>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Empty>
>((props, ref) => (
  <CommandPrimitive.Empty ref={ref} className="py-6 text-center text-sm" {...props} />
));
SqCommandEmpty.displayName = CommandPrimitive.Empty.displayName;

const SqCommandGroup = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Group>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Group>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Group
    ref={ref}
    className={cn(
      "overflow-hidden p-0 text-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground m-1",
      className,
    )}
    {...props}
  />
));

SqCommandGroup.displayName = CommandPrimitive.Group.displayName;

const SqCommandSeparator = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Separator
    ref={ref}
    className={cn("-mx-1 h-px bg-border", className)}
    {...props}
  />
));
SqCommandSeparator.displayName = CommandPrimitive.Separator.displayName;

interface SqCommandItemProps
  extends React.ComponentPropsWithoutRef<typeof CommandPrimitive.Item> {
  bgColor?: string;
  hoverBgColor?: string;
  radius?: number;
}

const SqCommandItem = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Item>,
  SqCommandItemProps
>(({ className, bgColor, hoverBgColor, radius = 6, style, ...props }, ref) => {
  const customCssVars = {
    ...(style || {}),
    ...(bgColor ? { "--sq-fill": bgColor } : {}),
    ...(hoverBgColor ? { "--sq-hover-fill": hoverBgColor } : {}),
    "--sq-radius": `${radius}px`,
  } as React.CSSProperties & Record<string, string>;

  const colorBindingClasses = cn(
    bgColor && "[--squircle-fill:var(--sq-fill)!important]",
    hoverBgColor &&
      "hover:[--squircle-fill:var(--sq-hover-fill)!important] focus:[--squircle-fill:var(--sq-hover-fill)!important]",
  );

  const radiusClasses = cn(`[--squircle-radius:var(--sq-radius)!important]`);

  return (
    <CommandPrimitive.Item
      ref={ref}
      className={cn(
        "relative flex cursor-default select-none items-center squircle squircle-radius-[6px] px-2 py-1.5 text-sm outline-none transition-colors hover:squircle-fill-hover-gray focus:squircle-fill-hover-gray data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        colorBindingClasses,
        radiusClasses,
        className,
      )}
      style={customCssVars}
      {...props}
    />
  );
});

SqCommandItem.displayName = CommandPrimitive.Item.displayName;

const SqCommandShortcut = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => {
  return (
    <span
      className={cn("ml-auto text-xs tracking-widest text-muted-foreground", className)}
      {...props}
    />
  );
};
SqCommandShortcut.displayName = "SqCommandShortcut";

export {
  SqCommand,
  SqCommandDialog,
  SqCommandInput,
  SqCommandList,
  SqCommandEmpty,
  SqCommandGroup,
  SqCommandItem,
  SqCommandShortcut,
  SqCommandSeparator,
};
